import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import isEmpty from 'lodash-es/isEmpty';

import {
  AccountingCalculationRequest,
  AccountingPlan,
  AccountingPlanPaySerieDTO,
  AccountingPlanResponse,
  AccountingRule,
  AccountingRuleResponse,
  EmpZoneData,
  PayArray,
  RegContractIndexCriteria,
  RegulationFlatten
} from './accounting.model';

import {REQUEST_OPTIONS} from '../../app.constants';
import {ApiRequestOptions, ApiService} from '../api';
import {Headers} from '../utils/headers';

@Injectable()
export class AccountingService {

  constructor(private api: ApiService) {
  }

  public getPayArrays(cliRegId: number, filters?: string): Observable<PayArray[]> {
    const filter = !isEmpty(filters) ? filters : '';
    return this.api.get('/repository/api/v1/regulation/' + cliRegId + '/flatten/payArrays' + filter)
      .pipe(
        map((response: RegulationFlatten) => {
          return response.regulationFlatten.payArrays;
        })
      );
  }

  public getAccountingRules(accountingPlanId: number, perPage: number, page: number, search: string, orderBy?: string)
    : Observable<AccountingRuleResponse> {
    const opts: ApiRequestOptions = REQUEST_OPTIONS.OBSERVE.RESPONSE;
    opts.requestOptions.params = {};

    if (perPage) {
      opts.requestOptions.params['per-page'] = perPage;
    }

    if (page) {
      opts.requestOptions.params['page'] = page;
    }

    if (search) {
      opts.requestOptions.params.search = search;
    }

    if (orderBy) {
      opts.requestOptions.params['order-by'] = orderBy;
    }

    return this.api.get('/payroll/api/v1/accounting-plan/' + accountingPlanId + '/accounting-rule', opts)
      .pipe(
        map((response: HttpResponse<AccountingRule[]>) => ({
          payload: <AccountingRule[]>response?.body,
          total: parseInt(Headers.get(response.headers, 'X-Total-Count'), 10)
        }))
      );
  }

  public getAccountingPlans(perPage: number, page: number, search = ''): Observable<AccountingPlanResponse> {
    const opts: ApiRequestOptions = REQUEST_OPTIONS.OBSERVE.RESPONSE;
    opts.requestOptions.params = {
      'per-page': perPage,
      'page': page
    };
    if (search) {
      opts.requestOptions.params.search = search;
    }

    return this.api.get('/payroll/api/v1/accounting-plan', opts)
      .pipe(
        map((response: HttpResponse<AccountingPlan[]>) => ({
          payload: <AccountingPlan[]>response?.body,
          total: parseInt(Headers.get(response.headers, 'X-Total-Count'), 10)
        }))
      );
  }

  public getAccountingCalculateResults(payPeriodId: number): Observable<AccountingCalculationRequest> {
    return this.api.get(`/payroll/api/v1/accounting-plan/pay-period/${payPeriodId}/calculate/results`);
  }

  public getAccountingPlan(id: number): Observable<AccountingPlan> {
    return this.api.get(`/payroll/api/v1/accounting-plan/${id}`);
  }

  public getRegContractIndexCriteria(): Observable<RegContractIndexCriteria[]> {
    return this.api
      .get(`/repository/api/v1/regulation/flatten?scope=RegContractIndexCriteria`)
      .pipe(
        map(response => response.regContractIndexCriteria)
      );
  }

  public getEmpZone(cliRegId: number): Observable<EmpZoneData[]> {
    return this.api
      .get(`/repository/api/v1/regulation/${cliRegId}/flatten/empZones`)
      .pipe(
        map(response => response.regulationFlatten.empZones)
      );
  }

  public saveAccountingPlan(accountingPlanPaySerieDto: AccountingPlanPaySerieDTO) {
    return this.api.put('/payroll/api/v1/accounting-plan', accountingPlanPaySerieDto);
  }

  public saveAccountingRule(accountingPlanId: number, accountingRule: AccountingRule) {
    return this.api.put('/payroll/api/v1/accounting-plan/' + accountingPlanId + '/accounting-rule', accountingRule);
  }

  public deleteAccountingPlan(accountingPlanId: number) {
    return this.api.delete('/payroll/api/v1/accounting-plan/' + accountingPlanId);
  }

  public deleteAccountingRule(accountingPlanId: number, accountingRuleId: number) {
    return this.api.delete('/payroll/api/v1/accounting-plan/' + accountingPlanId + '/accounting-rule/' + accountingRuleId);
  }

}
